function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useEffect, useState } from 'react';
import Decimal from 'decimal.js';
import { debounce } from 'lodash-es';
import { Controller, useForm } from 'react-hook-form';
import { requiredValidation } from 'validators';
import { Checkbox, ConfirmationModal, Divider, TextField, Typography } from 'seagull';
import { ErrorText } from "../../constants";
import { useOverlayContext, useStoreActions } from "../../contexts";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import { useRestfulWrapper } from "../../hooks/useRestfulWrapper";
import { ServerError } from "../../infra/ErrorResponse";
import { handleRequest } from "../../infra/utils";
import { helpArticles } from "../../utils/helpArticles";
import { connectControllerToTextField } from "../../utils/react-hook-form.utils";
import ExternalLinkButton from "../ExternalLinkButton";
import styles from "./RequestPartialRefund.module.scss";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const RequestPartialRefund = _ref => {
  let {
    relationship,
    id: invoiceId,
    rawTotalAmount,
    payment: invoicePayment,
    dismissModal,
    onSuccess,
    refreshInvoice
  } = _ref;
  const {
    api,
    logBI
  } = useStoreActions();
  const {
    REFUNDS_WITH_LINE_ITEMS
  } = useFeatureFlags();
  const [payment, setPayment] = useState(invoicePayment);
  const isCustomerFees = payment.isCustomerFees();
  const creditCardFees = payment.creditCardFees();
  const remainingAmount = payment.remainingAmount() || rawTotalAmount;
  useEffect(() => {
    setPayment(invoicePayment);
  }, [invoicePayment]);
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    setError,
    formState: {
      errors
    }
  } = useForm({
    mode: 'all',
    defaultValues: {
      refundAmount: remainingAmount,
      reason: '',
      refundFees: isCustomerFees
    }
  });
  const {
    startLoader,
    stopLoader,
    showErrorToast,
    showSuccessToast
  } = useOverlayContext();
  const {
    loading,
    refresh
  } = useRestfulWrapper({
    initialArgs: {
      refundAmount: remainingAmount.toString(),
      refundFees: isCustomerFees
    },
    fetch: async _ref2 => {
      let {
        refundAmount,
        refundFees
      } = _ref2;
      if (errors.refundAmount) return {
        refundAmount
      };
      const {
        success,
        data,
        error
      } = await handleRequest(api.invoice.calculateRefund(invoiceId, {
        amount: refundAmount,
        refundFees
      }));
      if (error || !success) return {
        refundAmount
      };
      return {
        refundAmount: data.refundAmount
      };
    }
  });
  const [totalRefundAmount, refundFees] = watch(['refundAmount', 'refundFees']);
  const businessDaysRange = payment.isFeesAch() && payment.isPaymentProcessorColumn() ? '1-2' : '5-10';
  useEffect(() => {
    const debouncedRefresh = debounce((refundAmount, refundFees) => {
      refresh({
        args: {
          refundAmount,
          refundFees
        }
      });
    }, 300);
    debouncedRefresh(totalRefundAmount.toString(), refundFees);
    return () => debouncedRefresh.cancel();
  }, [totalRefundAmount, refundFees]);
  return /*#__PURE__*/_jsx(ConfirmationModal, {
    testId: "request-refund-modal",
    secondaryButtonLabel: "Cancel",
    onPrimaryButtonClick: handleSubmit(async _ref3 => {
      let {
        reason,
        refundAmount,
        refundFees
      } = _ref3;
      startLoader();
      const refundRequest = REFUNDS_WITH_LINE_ITEMS ? api.invoice.refundPartialPaymentV2(invoiceId, {
        refundAmount: refundAmount.toString(),
        reason,
        refundFees
      }) : api.invoice.refundPartialPayment(invoiceId, {
        refundAmount: refundAmount.toString(),
        reason,
        refundFees
      });
      const {
        success,
        error
      } = await handleRequest(refundRequest);
      if (!success) {
        const {
          invoice
        } = await refreshInvoice();
        if (!invoice.payment) {
          showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'request-refund-error-toast');
          stopLoader();
          return;
        }
        setPayment(invoice.payment);
        const remainingAmount = payment.remainingAmount() || rawTotalAmount;
        if (!remainingAmount) {
          showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'request-refund-error-toast');
          stopLoader();
          return;
        }
        if (error !== null && error !== void 0 && error.hasServerError(ServerError.InvoiceRefundAmountExceeds)) {
          setError('refundAmount', {
            message: "Amount cannot be more than: $".concat(remainingAmount.toString())
          });
          showErrorToast('The amount to refund exceeds the remaining amount of the payment.', 'request-refund-error-toast');
        } else if (error !== null && error !== void 0 && error.hasServerError(ServerError.InvoiceAlreadyHasOneRefundNotRefunded)) {
          showErrorToast('A previous refund is still processing. Please try again in one hour.', 'request-refund-error-toast');
        } else if (error !== null && error !== void 0 && error.hasServerError(ServerError.InvoiceIsFullyRefunded)) {
          dismissModal();
          showErrorToast('The invoice is fully refunded.', 'request-refund-error-toast');
        } else {
          showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'request-refund-error-toast');
        }
      } else {
        onSuccess({
          reason,
          refundAmount: refundAmount.toString(),
          refundFees
        });
        dismissModal();
        showSuccessToast('Refund request submitted', 'request-refund-success-toast');
        const isFullRefund = new Decimal(refundAmount).eq(new Decimal(rawTotalAmount));
        const relationshipId = relationship.id;
        logBI(isFullRefund ? 19000 : 19001, {
          relationshipId
        });
      }
      stopLoader();
    }),
    onSecondaryButtonClick: dismissModal,
    primaryButtonTestId: "request-refund-submit",
    secondaryButtonTestId: "request-refund-cancel",
    title: "Refund payment",
    primaryButtonLabel: "Refund",
    width: "720",
    primaryButtonLoading: loading,
    content: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs("div", {
        className: styles.fieldsWrapper,
        children: [/*#__PURE__*/_jsx(Controller, {
          control: control,
          name: "refundAmount",
          rules: {
            validate: value => {
              if (!value) return 'Please enter a valid amount';
              const amount = new Decimal(value);
              if (amount.gt(remainingAmount)) return "Amount cannot be more than: $".concat(remainingAmount.toString());
              if (amount.lte(0)) return "Amount must be greater than: $0";
              return true;
            }
          },
          render: props => /*#__PURE__*/_jsx(TextField, _objectSpread({
            collapseHelperText: false,
            width: "45%",
            autoFocus: true,
            isMoney: true,
            prefix: "$",
            label: "Amount to refund",
            testId: "request-refund-refund-amount"
          }, connectControllerToTextField(props)))
        }), payment.isCustomerFees() && /*#__PURE__*/_jsx(Controller, {
          control: control,
          name: "refundFees",
          render: props => /*#__PURE__*/_jsx(Checkbox, {
            onChange: e => {
              setValue('refundFees', e.target.checked);
            },
            checked: props.field.value,
            className: styles.checkbox,
            testId: "refund-fees-checkbox",
            children: /*#__PURE__*/_jsxs("div", {
              children: [/*#__PURE__*/_jsx(Typography, {
                align: "left",
                children: "Reimburse the client for credit card processing fees"
              }), /*#__PURE__*/_jsxs(Typography, {
                skin: "secondary",
                align: "left",
                testId: "refund-fees-info",
                children: ["Your client paid an extra $", creditCardFees === null || creditCardFees === void 0 ? void 0 : creditCardFees.feesAmount, " in credit card processing fees for this payment. For partial refunds, the processing fees are reimbursed proportionally to the refund amount."]
              })]
            })
          })
        })]
      }), /*#__PURE__*/_jsx(Divider, {}), /*#__PURE__*/_jsxs("div", {
        className: styles.reasonContainer,
        children: [/*#__PURE__*/_jsx(Typography, {
          weight: "bold",
          variant: "h6",
          children: "What\u2019s the reason for this refund?"
        }), /*#__PURE__*/_jsx(Typography, {
          children: "This is internal and will not show up for your client"
        }), /*#__PURE__*/_jsx(Controller, {
          control: control,
          name: "reason",
          rules: requiredValidation('Please provide a reason'),
          render: props => /*#__PURE__*/_jsx("div", {
            className: styles.inputField,
            children: /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({
              collapseHelperText: false,
              multiline: true,
              minRows: 2,
              maxRows: 3
            }, connectControllerToTextField(props)), {}, {
              testId: "request-refund-reason-field"
            }))
          })
        }), /*#__PURE__*/_jsxs(Typography, {
          className: styles.infoParagraph,
          testId: "request-refund-business-days-range",
          children: ["Refunds are deducted from future payouts when possible. If not, funds will be withdrawn from your account to cover the balance. Refunds may take ", businessDaysRange, " business days to appear in the customer's bank account, and fees may apply.", ' ', /*#__PURE__*/_jsx(ExternalLinkButton, {
            openInNewTab: true,
            href: helpArticles.refunds(),
            underline: "none",
            children: "Learn more"
          })]
        })]
      })]
    })
  });
};
export default RequestPartialRefund;